'use client';

import React from 'react';
import dynamic from 'next/dynamic';

import numeral from 'numeral';
import { useIntersection } from 'react-use';
import { useCountUp } from 'use-count-up';

import { ConfettiProps } from '~/components/common/confetti';
import { cn } from '~/lib/utils';
import { BlockThermometerFragment } from '~/server/generated/contentful-graphql';
import { clamp } from '~/utils/number';

import { StarSVG } from './star-svg';
import { ThermometerSVG } from './thermometer-svg';

const Confetti = dynamic<ConfettiProps>(() => import('~/components/common/confetti').then((mod) => mod.Confetti), {
  ssr: false,
});

export function Thermometer({ block }: { block: BlockThermometerFragment }) {
  const [explode, setExplode] = React.useState(false);
  const hasCelebrated = React.useRef(false);

  const amountGoal = Number(block?.amountGoal);
  const amountRaised = Number(block?.amountRaised);
  const milestones = block?.milestones
    ? [...block.milestones.flatMap((m) => Number(m) ?? []), amountGoal]
    : [amountGoal];

  const ref = React.useRef<HTMLDivElement>(null!);

  // Visibility
  const intersection = useIntersection(ref, {
    root: null,
    rootMargin: '0px',
    threshold: 0.5,
  });

  const inView = (intersection?.intersectionRatio ?? 0) >= 0.5;

  const { value } = useCountUp({
    isCounting: inView,
    end: amountRaised,
    start: 0,
    duration: 1,
  });

  // fix incorrect type for `value`
  const valueNum = Number(value);

  const countUpComplete = amountRaised === valueNum;

  const milestonesReached = milestones.filter((m) => valueNum >= m);
  const milestoneReached = milestonesReached[milestonesReached.length - 1];
  const milestonesDividers = milestones;

  const fractionRaised = clamp(0, 1, valueNum / amountGoal);
  const percentRaised = fractionRaised * 100;
  const goalReached = valueNum >= amountGoal;
  const goalExceeded = valueNum > amountGoal;

  React.useEffect(() => {
    if (hasCelebrated.current || !inView || !countUpComplete) return;
    if (goalReached || milestoneReached) {
      hasCelebrated.current = true;
      setExplode(true);
    }
  }, [inView, goalReached, milestoneReached, countUpComplete]);

  return (
    <div className='container xl:max-w-7xl' ref={ref}>
      <div className='relative z-10 mx-auto max-w-xs sm:max-w-sm md:max-w-lg lg:max-w-2xl'>
        <div className='relative pt-6'>
          {milestonesDividers.map((milestone) => (
            <div
              className={cn(
                'left-(--left) font-greycliff-extrabold absolute top-0 z-20 -translate-x-1/2 uppercase leading-none transition-colors duration-700 ease-out',
                milestonesReached.findIndex((m) => m === milestone) == -1 && 'text-asphalt',
                milestonesReached.findIndex((m) => m === milestone) !== -1 && 'text-primary',
                milestone === amountGoal && '-translate-x-full!',
              )}
              key={milestone}
              style={
                {
                  '--left': (milestone / amountGoal) * 100 + '%',
                } as React.CSSProperties
              }
            >
              ${numeral(milestone).format('0[.]0a')}
            </div>
          ))}
          <div className='relative'>
            <div
              className={cn(
                'absolute right-0 top-1/2 -z-10 h-24 w-64 origin-center -translate-y-1/2 translate-x-1/2 rotate-0 transition-all md:h-32 lg:h-44 xl:h-64',
                goalReached ? 'opacity-1 scale-1' : 'scale-0 opacity-0',
              )}
            >
              <StarSVG />
            </div>
            <ThermometerSVG width={percentRaised} />
            {milestonesDividers
              .filter((milestone) => milestone !== amountGoal)
              .map((milestone) => (
                <div
                  className='left-(--left) absolute inset-y-0 z-10 w-px bg-white'
                  key={`milestone-${milestone}`}
                  style={
                    {
                      '--left': (milestone / amountGoal) * 100 + '%',
                    } as React.CSSProperties
                  }
                />
              ))}
          </div>
        </div>
      </div>
      {countUpComplete && !goalReached && (
        <div className='animate-in fade-in fill-mode-both relative mt-8 text-center text-xl duration-700 ease-out motion-reduce:animate-none'>
          {amountRaised === 0 && <h2>Let&pos;s start fundraising!</h2>}
          {amountRaised > 0 && !milestoneReached && <h2>We&pos;re doing great!</h2>}
          {amountRaised > 0 && milestoneReached && (
            <div className='heading-lg text-foreground'>
              ${numeral(milestoneReached).format('0[.]0a')} Milestone Reached!
            </div>
          )}
          <div className='mt-4'>Total raised: ${numeral(amountRaised).format()}</div>
        </div>
      )}
      {countUpComplete && goalReached && (
        <div className='animate-in fade-in fill-mode-both relative mt-8 text-center text-xl duration-700 ease-out motion-reduce:animate-none'>
          <h2>{goalExceeded ? "Amazing! We've exceeded our goal!" : "Congrats! We've met our goal!"}</h2>
          <div>
            Total raised:{' '}
            <span className={cn(goalExceeded && 'greycliffcf-extrabold text-primary')}>
              ${numeral(amountRaised).format()}
            </span>
          </div>
        </div>
      )}
      <Confetti
        numberOfPieces={explode ? Math.round(400 * fractionRaised) : 0}
        onConfettiComplete={(confetti) => {
          setExplode(false);
          confetti?.reset();
        }}
        recycle={false}
        run={explode}
        shape={block.confettiType?.toUpperCase() === 'GIFTS' ? 'gift' : 'star'}
      />
    </div>
  );
}
